import { Card } from '@/components/ui/Card'
import SiluxPromise from '@/components/SiluxPromise'

export default function About() {
  return (
    <div className="max-w-6xl mx-auto py-12 px-4">
      <h1 className="text-3xl font-bold text-center mb-8">About Silux Mobile</h1>

      <SiluxPromise variant="all" />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
        <div>
          <h2 className="text-2xl font-semibold mb-4">Our Story</h2>
          <p className="text-gray-600 leading-relaxed mb-4">
            Founded in 2020, Silux Mobile emerged from a simple idea: to provide UK consumers with
            affordable, reliable mobile and broadband services without the hassle of traditional
            telecom providers. We believe that staying connected shouldn't come with hidden fees,
            confusing contracts, or poor customer service.
          </p>
          <p className="text-gray-600 leading-relaxed">
            Today, we serve thousands of customers across the United Kingdom, offering SIM-only
            plans, broadband services, and bundled packages that deliver exceptional value. Our
            commitment to transparency, quality, and customer satisfaction has made us one of the
            fastest-growing providers of SIM and Mobile Broadband Services in the UK.
          </p>
        </div>
        <div className="bg-gradient-to-br from-silux-primary to-primary-500 rounded-lg p-8 text-white flex flex-col justify-center">
          <h3 className="text-xl font-semibold mb-4">Our Mission</h3>
          <p className="leading-relaxed">
            To democratize telecommunications by providing accessible, transparent, and high-quality
            mobile and broadband services that empower people to stay connected with those who matter most.
          </p>
        </div>
      </div>

      <div className="mb-12">
        <h2 className="text-2xl font-semibold text-center mb-8">Why Choose Silux Mobile?</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card className="p-6 text-center">
            <div className="w-16 h-16 bg-silux-light rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-silux-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h3 className="font-semibold mb-2">Transparent Pricing</h3>
            <p className="text-gray-600 text-sm">
              No hidden fees or surprise charges. What you see is what you pay, with all costs
              clearly displayed upfront.
            </p>
          </Card>

          <Card className="p-6 text-center">
            <div className="w-16 h-16 bg-silux-light rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-silux-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
            </div>
            <h3 className="font-semibold mb-2">Reliable Network</h3>
            <p className="text-gray-600 text-sm">
              We partner with major UK networks (EE, Vodafone, O2, Three) to ensure you get
              the best coverage wherever you are.
            </p>
          </Card>

          <Card className="p-6 text-center">
            <div className="w-16 h-16 bg-silux-light rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-silux-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z" />
              </svg>
            </div>
            <h3 className="font-semibold mb-2">24-Hour Chat Support</h3>
            <p className="text-gray-600 text-sm">
              Chat support is available 24 hours a day. Our UK-based team is here Monday to Friday,
              9am to 5pm (closed weekends and bank holidays) for technical and billing queries.
            </p>
          </Card>
        </div>
      </div>

      <div className="mb-12">
        <h2 className="text-2xl font-semibold text-center mb-8">Our Values</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="flex gap-4">
            <div className="flex-shrink-0 w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
              <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              <h3 className="font-semibold mb-1">Integrity</h3>
              <p className="text-gray-600 text-sm">We believe in honest dealings and transparent business practices.</p>
            </div>
          </div>
          <div className="flex gap-4">
            <div className="flex-shrink-0 w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
            </div>
            <div>
              <h3 className="font-semibold mb-1">Innovation</h3>
              <p className="text-gray-600 text-sm">We continuously improve our services to meet evolving customer needs.</p>
            </div>
          </div>
          <div className="flex gap-4">
            <div className="flex-shrink-0 w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center">
              <svg className="w-6 h-6 text-primary-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </div>
            <div>
              <h3 className="font-semibold mb-1">Customer First</h3>
              <p className="text-gray-600 text-sm">Every decision we make puts our customers' interests at the forefront.</p>
            </div>
          </div>
          <div className="flex gap-4">
            <div className="flex-shrink-0 w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
              <svg className="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              <h3 className="font-semibold mb-1">Sustainability</h3>
              <p className="text-gray-600 text-sm">We're committed to reducing our environmental impact through eco-friendly practices.</p>
            </div>
          </div>
        </div>
      </div>

      <Card className="p-8 bg-gray-50">
        <h2 className="text-2xl font-semibold text-center mb-6">Company Information</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div>
            <h3 className="font-semibold mb-3">Legal Details</h3>
            <ul className="space-y-2 text-sm text-gray-600">
              <li><strong>Company Name:</strong> Silux Control UK Ltd T/A Silux Mobile</li>
              <li><strong>Company Number:</strong> 11901981</li>
              <li><strong>VAT Number:</strong> GB 321 2589 23</li>
              <li><strong>Registered in:</strong> England and Wales</li>
              <li><strong>ICO Registration:</strong> ZB525529</li>
            </ul>
          </div>
          <div>
            <h3 className="font-semibold mb-3">Registered Office</h3>
            <address className="not-italic text-sm text-gray-600">
              Silux Mobile<br />
              13 Prospect House<br />
              Colliery Close, Staveley<br />
              Chesterfield<br />
              S43 3QE<br />
              United Kingdom
            </address>
          </div>
        </div>
      </Card>

      <div className="mt-12 text-center">
        <h2 className="text-2xl font-semibold mb-4">Ready to Get Started?</h2>
        <p className="text-gray-600 mb-6">
          Join thousands of satisfied customers and experience the Silux Mobile difference.
        </p>
        <div className="flex justify-center gap-4">
          <a
            href="/shop"
            className="px-8 py-3 bg-silux-primary text-white rounded-lg hover:bg-opacity-90 transition-colors font-medium"
          >
            View Our Plans
          </a>
          <a
            href="/contact"
            className="px-8 py-3 border border-silux-primary text-silux-primary rounded-lg hover:bg-silux-light transition-colors font-medium"
          >
            Contact Us
          </a>
        </div>
      </div>
    </div>
  )
}
